import React, { useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle, ArrowLeft, ArrowRight, CalendarDays, Check, Clock, History,
  Loader2, Lock, Repeat, Soup, Tag, TrendingUp, Users, UtensilsCrossed, X,
} from 'lucide-react';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { TimePicker } from '../../components/ui/time-picker';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import {
  WEEK, WEEK_LONG, addDays, iso, longDate, monday0, rupees, shortDate, str, t12,
} from './openingHelpers';
import apiClient from '../../services/api';

/** The opening wizard in three steps; the buffet opens AS DESIGNED, with the day deciding only covers and which dishes. */

const NEW_STEPS = [
  ['buffet', 'Which buffet'],
  ['when', 'Date & repeat'],
  ['review', 'Menu items & qty'],
];
const EDIT_STEPS = [
  ['when', 'Date & time'],
  ['review', 'Menu items & qty'],
];

const REPEAT_MODES = [
  ['none', 'Just this date', 'one service, on the day you picked'],
  ['daily', 'Every day', 'the buffet runs daily until the end date'],
  ['days', 'Chosen days', 'only the days of the week you tick'],
];

const MAX_REPEAT_DAYS = 366;   // mirrors MAX_REPEAT_DAYS in openings_controller

/* Every date a repeat asks for, by the same rule the server applies, so the calendar's highlights are what opens. */
export function repeatDates(startISO, repeat) {
  if (!startISO) return [];
  const mode = repeat?.mode || 'none';
  if (mode === 'none') return [startISO];
  const until = repeat?.until || '';
  if (!until || until < startISO) return [];
  const start = new Date(`${startISO}T00:00:00`);
  const end = new Date(`${until}T00:00:00`);
  if ((end - start) / 86400000 > MAX_REPEAT_DAYS) return [];
  const wanted = mode === 'daily' ? [0, 1, 2, 3, 4, 5, 6] : (repeat?.days || []);
  if (!wanted.length) return [];
  const out = [];
  for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
    if (wanted.includes(monday0(d))) out.push(iso(d));
  }
  return out;
}

const qtyLabel = (i) => {
  const q = Number(i.qty) || 0;
  return i.unit === 'pcs' ? `${Math.round(q)} pcs` : `${q} ${i.unit || 'kg'}`;
};

/* Module-level, NOT declared inside the wizard: a component defined during render loses focus every keystroke. */
function Field({ label, hint, children }) {
  return (
    <div>
      <Label className="text-sm text-muted-foreground">{label}</Label>
      {children}
      {hint && <p className="mt-1 text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}

function Pill({ on, onClick, children, title }) {
  return (
    <button type="button" onClick={onClick} title={title} aria-pressed={on}
      className={`rounded-full border px-3 py-1 text-xs font-medium transition ${
        on ? 'border-primary bg-primary text-primary-foreground'
          : 'border-border bg-background text-muted-foreground hover:bg-muted'}`}>
      {children}
    </button>
  );
}

/* The settings the day inherits, shown as facts rather than boxes; this screen is not where they change. */
function FromTemplate({ name, adultPrice, childPrice, covers, dishes }) {
  const bits = [
    [UtensilsCrossed, name],
    [Tag, adultPrice != null && adultPrice !== ''
      ? `${rupees(adultPrice)} / adult${childPrice != null && childPrice !== '' ? ` · ${rupees(childPrice)} / kid` : ''}`
      : 'no per-head price set'],
    [Users, covers ? `${covers} guests` : 'no cover count set'],
    [Soup, `${dishes} dish${dishes === 1 ? '' : 'es'} on the counter`],
  ];
  return (
    <div className="rounded-lg border border-border bg-muted/30 p-3">
      <div className="mb-2 flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
        <Lock size={12} /> From the buffet template — copied onto this day as it is
      </div>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5">
        {bits.map(([Icon, text], i) => (
          <span key={i} className="inline-flex items-center gap-1.5 text-sm text-foreground">
            <Icon size={13} className="text-muted-foreground" /> {text}
          </span>
        ))}
      </div>
    </div>
  );
}

export default function OpeningWizard({
  restaurantId, date, templates = [], opening = null,
  saving = false, onCancel, onSave, onRepeatPreview,
}) {
  const steps = opening ? EDIT_STEPS : NEW_STEPS;
  const [stepIx, setStepIx] = useState(0);
  const [tpl, setTpl] = useState(null);
  const [warn, setWarn] = useState('');

  // What this screen decides: the hours, the covers, and which dishes go out.
  /* Adults and children are counted apart because they EAT apart; the covers sent to the server are their sum. */
  const [form, setForm] = useState(() => {
    const total = opening?.covers_planned;
    /* A day saved before the split has kids_planned null, so the kid box starts blank rather than inventing a 0. */
    const kids = opening?.kids_planned;
    return {
      start_time: str(opening?.start_time),
      end_time: str(opening?.end_time),
      adults: total != null ? String(Math.max(0, total - (kids || 0))) : '',
      kids: kids != null ? String(kids) : '',
    };
  });
  /* Dishes this day is NOT serving, by name; quantities are never in here, since the template owns how much. */
  const [excluded, setExcluded] = useState(() => new Set());
  /* The counter for the covers currently typed, straight from the server, since JS rounding would make it lie. */
  const [preview, setPreview] = useState({ loading: false, items: null, forCovers: null });
  const [histOf, setHistOf] = useState(null);          // dish name, or null
  const [hist, setHist] = useState({});                // dish -> rows
  const [bookings, setBookings] = useState(null);      // null = never read
  const [bookingsOpen, setBookingsOpen] = useState(false);
  const [repeat, setRepeat] = useState({ mode: 'none', days: [], until: '' });

  const step = steps[stepIx][0];
  const src = opening || tpl;                       // where the day's values come from
  const items = useMemo(() => (opening ? opening.items : tpl?.items) || [], [opening, tpl]);

  const set = (k, v) => { setWarn(''); setForm((p) => ({ ...p, [k]: v })); };

  /* Picking a template takes its hours as the starting point; the rest is read on the server when the day is written. */
  const chooseTemplate = (t) => {
    setTpl(t);
    /* Its hours AND its guest split, so a template for 120 adults and 9 children doesn't open on 129 adults. */
    const total = Number(t.covers_planned) || 0;
    const kids = Math.min(Number(t.kids_planned) || 0, total);
    setForm((p) => ({
      ...p,
      start_time: str(t.start_time),
      end_time: str(t.end_time),
      adults: total ? String(total - kids) : '',
      /* No kid split on the template leaves the box EMPTY behind its "0"
         placeholder — a seeded literal 0 would have to be deleted before
         typing, and blank means the same thing everywhere downstream. */
      kids: total && kids ? String(kids) : '',
    }));
    setStepIx(1);
  };

  /* The dates a pending repeat would open on, handed to the calendar so the run is checked by looking at the month. */
  const dates = useMemo(() => repeatDates(date, repeat), [date, repeat]);
  useEffect(() => {
    if (!onRepeatPreview) return;
    onRepeatPreview(!opening && repeat.mode !== 'none' ? dates : []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dates, repeat.mode, opening]);
  useEffect(() => () => onRepeatPreview && onRepeatPreview([]),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []);

  // The counter as the template lays it out, course by course in menu order, never re-sorted here.
  const grouped = useMemo(() => {
    const out = [];
    const at = {};
    // The previewed counter once it lands, the template's list until then - every dish, unticked ones included.
    (preview.items || items).forEach((i) => {
      const c = i.course || 'Other';
      if (at[c] === undefined) { at[c] = out.length; out.push([c, []]); }
      out[at[c]][1].push(i);
    });
    return out;
  }, [preview.items, items]);

  /* Typed counts win; an untouched form falls back to what the template or the opening already plans for. */
  const adults = form.adults !== '' ? Number(form.adults) || 0 : null;
  const kids = form.kids !== '' ? Number(form.kids) || 0 : null;
  const covers = adults != null || kids != null
    ? (adults || 0) + (kids || 0)
    : (Number(src?.covers_planned) || 0);
  const buffetId = opening ? opening.buffet_id : tpl?.id;
  /* Ask the server what these covers put on the counter, debounced, since the box is typed digit by digit. */
  useEffect(() => {
    if (!restaurantId || !buffetId || step !== 'review') return undefined;
    const key = `${covers}/${kids ?? ''}`;
    if (preview.forCovers === key) return undefined;
    let alive = true;
    setPreview((p) => ({ ...p, loading: true }));
    const t = setTimeout(() => {
      apiClient.get(`/users/restaurant/${restaurantId}/buffets/${buffetId}/spread-preview/`,
        { params: { date, covers, ...(kids != null ? { kids } : {}) } })
        .then(({ data }) => alive && setPreview({
          loading: false,
          items: data?.success ? (data.data.items || []) : [],
          bufferPct: data?.success ? (data.data.safety_buffer_pct ?? null) : null,
          forCovers: key,
        }))
        .catch(() => alive && setPreview({ loading: false, items: [], bufferPct: null, forCovers: key }));
    }, 350);
    return () => { alive = false; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restaurantId, buffetId, date, covers, kids, step]);

  /* What the day will actually serve: the previewed counter, or the template's
     list, minus what is unticked — and never a dish the STUDIO has switched
     off. Those ride along flagged template_off so the sheet can show them,
     but a day cannot serve what its template does not offer. */
  const spread = useMemo(
    () => (preview.items || items).filter((i) => !i.template_off
      && !(i.live_counter && i.qty == null)   // a station with nothing planned preps nothing
      && !excluded.has(i.item)),
    [preview.items, items, excluded]);
  /* Said apart in every count: an ingredient a station preps is not a dish,
     and "11 dishes · 2 live counters" is what the kitchen actually reads. */
  const dishesOut = useMemo(() => spread.filter((i) => !i.live_counter), [spread]);
  const countersOut = useMemo(() => new Set(
    (preview.items || items)
      .filter((i) => i.live_counter && !excluded.has(i.item))
      .map((i) => i.course),
  ).size, [preview.items, items, excluded]);

  /* A dish's past opens in its own window, since a report unfolded between rows buries the list it came from. */
  const openHistory = (dish) => {
    setHistOf(dish);
    if (hist[dish] || !restaurantId) return;
    apiClient.get(`/users/restaurant/${restaurantId}/buffets/dish-history/`,
      { params: { item: dish } })
      .then(({ data }) => setHist((p) => ({ ...p, [dish]: data?.success ? (data.data.rows || []) : [] })))
      .catch(() => setHist((p) => ({ ...p, [dish]: [] })));
  };

  /* What the ledger adds up to, so the window opens with the answer rather than rows to total by eye. */
  const histSummary = useMemo(() => {
    const rows = hist[histOf];
    if (!rows || !rows.length) return null;
    const eaten = rows.filter((r) => Number(r.consumed_qty) > 0);
    const total = eaten.reduce((n, r) => n + (Number(r.consumed_qty) || 0), 0);
    // Ran past what was cooked - the rows worth spotting when deciding a quantity, counted for the reader.
    const over = rows.filter((r) => r.planned_qty != null
      && Number(r.consumed_qty) > Number(r.planned_qty)).length;
    return {
      services: rows.length,
      avg: eaten.length ? total / eaten.length : null,
      over,
      unit: rows[0].unit || 'kg',
    };
  }, [hist, histOf]);

  /* Ninety days of who booked and who came, in its own window so it doesn't push the step off screen. */
  const openBookings = () => {
    setBookingsOpen(true);
    if (bookings || !restaurantId || !buffetId) return;
    setBookings({ loading: true, rows: [], avg: null });
    apiClient.get(`/users/restaurant/${restaurantId}/buffets/${buffetId}/booking-history/`,
      { params: { days: 90 } })
      .then(({ data }) => setBookings({
        loading: false,
        rows: data?.success ? (data.data.rows || []) : [],
        avg: data?.success ? data.data.avg_fed : null,
      }))
      .catch(() => setBookings({ loading: false, rows: [], avg: null }));
  };

  /* What ninety days add up to: the averages the covers boxes are reaching for, printed rather than derived. */
  const bookingSummary = useMemo(() => {
    const rows = bookings?.rows;
    if (!rows || !rows.length) return null;
    const sum = (k) => rows.reduce((n, r) => n + (Number(r[k]) || 0), 0);
    /* Fed is the till's count through the door, booked arrivals included, so adding the booked column would double-count. */
    const served = rows.map((r) => Number(r.covers_sold) || 0).filter((n) => n > 0);
    return {
      services: rows.length,
      avgWalkIn: rows.length ? Math.round(sum('walk_ins') / rows.length) : 0,
      avgBooked: rows.length ? Math.round(sum('booked_heads') / rows.length) : 0,
      busiest: served.length ? Math.max(...served) : 0,
      quietest: served.length ? Math.min(...served) : 0,
    };
  }, [bookings]);

  const toggleDish = (name) => setExcluded((p) => {
    const n = new Set(p);
    if (n.has(name)) n.delete(name); else n.add(name);
    return n;
  });

  const problemWith = (which) => {
    if (which === 'buffet' && !tpl) return 'Pick the buffet you are opening.';
    if (which === 'when' && form.start_time && form.end_time
      && form.end_time <= form.start_time) {
      return 'The buffet has to end after it starts.';
    }
    if (which === 'when' && !(covers > 0)) {
      return 'Say how many adults and children this day is prepped for — every quantity is worked out from them.';
    }
    /* The repeat is part of this step now, so it is checked here; submit walks only the steps that exist. */
    if (which === 'when' && !opening && repeat.mode !== 'none') {
      if (!repeat.until) return 'Say which date the repeat runs until.';
      if (repeat.mode === 'days' && !repeat.days.length) {
        return 'Tick at least one day of the week.';
      }
      if (!dates.length) return 'No dates fall in that repeat — check the days and the end date.';
    }
    if (which === 'review' && spread.length === 0) {
      return 'Tick at least one dish — a service with an empty counter cannot open.';
    }
    return '';
  };

  const go = (delta) => {
    if (delta > 0) {
      const msg = problemWith(step);
      if (msg) { setWarn(msg); return; }
    }
    setWarn('');
    setStepIx((i) => Math.min(steps.length - 1, Math.max(0, i + delta)));
  };

  const submit = () => {
    for (let i = 0; i < steps.length; i += 1) {
      const msg = problemWith(steps[i][0]);
      if (msg) { setWarn(msg); setStepIx(i); return; }
    }
    /* Only what this screen decides is sent; everything else comes from the template, which is what "as designed" means. */
    /* `notes` is deliberately absent rather than empty, so an unrelated edit can't blank an older opening's note. */
    const body = {
      start_time: str(form.start_time).trim(),
      end_time: str(form.end_time).trim(),
      covers_planned: covers,
      // The split, not just the total: it changes the portions; omitted when unsaid, so the template's proportion stands.
      ...(kids != null ? { kids_planned: kids } : {}),
      // Names, not quantities: the server recomputes the counter, so nothing stored is a number typed on this form.
      excluded_items: [...excluded],
    };
    if (!opening) {
      body.buffet_id = tpl.id;
      body.date = date;
      body.repeat = repeat.mode === 'none' ? { mode: 'none' }
        : { mode: repeat.mode, days: repeat.days, until: repeat.until };
    }
    onSave(body);
  };

  const setUntil = (v) => { setWarn(''); setRepeat((p) => ({ ...p, until: v })); };
  const setMode = (mode) => setRepeat((p) => ({
    mode,
    // Chosen days starts on the day you are standing on - the commonest repeat is "this day, every week".
    days: p.days.length ? p.days : [monday0(new Date(`${date}T00:00:00`))],
    until: p.until || addDays(date, 28),
  }));
  const toggleDay = (d) => setRepeat((p) => ({
    ...p,
    days: p.days.includes(d) ? p.days.filter((x) => x !== d) : [...p.days, d].sort(),
  }));

  return (
    <div className="rounded-xl border border-primary/40 bg-primary/[0.03] p-4">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <UtensilsCrossed size={16} className="text-primary" />
        <span className="font-semibold text-foreground">
          {opening ? `${opening.name} — this day` : tpl ? `Creating ${tpl.name}` : 'Create a buffet'}
        </span>
        <span className="text-sm text-muted-foreground">· {longDate(date)}</span>
        <button type="button" onClick={onCancel} disabled={saving}
          className="ml-auto rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
          aria-label="Close">
          <X size={16} />
        </button>
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-1.5">
        {steps.map(([k, label], i) => {
          const done = i < stepIx;
          const now = i === stepIx;
          return (
            <React.Fragment key={k}>
              <button
                type="button"
                onClick={() => { if (i < stepIx || (tpl || opening)) { setWarn(''); setStepIx(i); } }}
                disabled={i > stepIx && !tpl && !opening}
                className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition ${
                  now ? 'border-primary bg-primary text-primary-foreground'
                    : done ? 'border-primary/40 bg-primary/10 text-foreground hover:bg-primary/20'
                      : 'border-border bg-background text-muted-foreground disabled:opacity-50'}`}
              >
                <span className={`flex h-4 w-4 items-center justify-center rounded-full text-[10px] ${
                  now ? 'bg-primary-foreground/20' : done ? 'bg-primary/20' : 'bg-muted'}`}>
                  {done ? <Check size={10} /> : i + 1}
                </span>
                {label}
              </button>
              {i < steps.length - 1 && <span className="text-muted-foreground/40">·</span>}
            </React.Fragment>
          );
        })}
      </div>

      {warn && (
        <div className="mb-3 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
          <AlertTriangle size={15} className="mt-0.5 shrink-0" /> {warn}
        </div>
      )}

      {/* ── 1 · which buffet ───────────────────────────────────────────── */}
      {step === 'buffet' && (
        <div>
          {templates.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              All templates are open on this date. Add one under
              <strong className="text-foreground"> Buffet Templates</strong>.
            </p>
          ) : (
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-3">
              {templates.map((t) => (
                <button key={t.id} type="button" onClick={() => chooseTemplate(t)}
                  className={`rounded-lg border p-3 text-left transition ${
                    tpl?.id === t.id ? 'border-primary bg-primary/10'
                      : 'border-border hover:border-primary hover:bg-primary/5'}`}>
                  <div className="font-medium text-foreground">{t.name}</div>
                  <div className="mt-0.5 text-xs text-muted-foreground">
                    {t.start_time ? `${t12(t.start_time)} – ${t12(t.end_time)}` : 'no hours set'}
                    {t.adult_price != null && ` · ${rupees(t.adult_price)} / adult`}
                  </div>
                  <div className="mt-0.5 text-xs text-muted-foreground">
                    {t.covers_planned ? `${t.covers_planned} guests` : 'no cover count'}
                    {' · '}
                    {/* Only what would actually go out: the map also carries the studio's switched-off dishes, which serve nobody. */}
                    {(t.items || []).filter((i) => !i.template_off).length} dish
                    {(t.items || []).filter((i) => !i.template_off).length === 1 ? '' : 'es'}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── 2 · date, time & repeat ────────────────────────────────────── */}
      {step === 'when' && (
        <div>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
            <Field label="Date">
              <div className="mt-1 flex h-10 items-center gap-2 rounded-md border border-input bg-muted/40 px-3 text-sm text-foreground">
                <CalendarDays size={14} className="text-muted-foreground" /> {longDate(date)}
              </div>
            </Field>
            <Field label="Starts">
              <TimePicker className="mt-1" value={form.start_time} step={15}
                onChange={(v) => set('start_time', v)} />
            </Field>
            <Field label="Ends">
              <TimePicker className="mt-1" value={form.end_time} step={15}
                min={form.start_time || undefined}
                onChange={(v) => set('end_time', v)} />
            </Field>
          </div>

          {/* Counted apart because they eat apart; text boxes, since a scroll wheel would re-work a whole prep sheet. */}
          <div className="mt-4 flex flex-wrap items-end gap-3">
            <Field label="Adults">
              <Input className="mt-1 w-28 tabular-nums" type="text" inputMode="numeric"
                value={form.adults} placeholder="0"
                onChange={(e) => set('adults', e.target.value.replace(/\D+/g, '').slice(0, 5))} />
            </Field>
            <Field label="Children">
              <Input className="mt-1 w-28 tabular-nums" type="text" inputMode="numeric"
                value={form.kids} placeholder="0"
                onChange={(e) => set('kids', e.target.value.replace(/\D+/g, '').slice(0, 5))} />
            </Field>
            <span className="pb-2 text-sm text-muted-foreground">
              = <strong className="tabular-nums text-foreground">{covers || '—'}</strong> guests
            </span>
            {buffetId && (
              <button type="button" onClick={openBookings} aria-haspopup="dialog"
                title="What this buffet has actually drawn over the last 90 days"
                className="mb-1 inline-flex items-center gap-1.5 rounded-md border border-border px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50">
                <History size={13} /> Booking history
              </button>
            )}
          </div>

          {/* The run this date starts, asked beside the date; left out when editing a day that already exists. */}
          {!opening && (
            <div className="mt-4 border-t border-border/60 pt-4">
              <div className="mb-2 flex items-center gap-1.5 text-sm font-medium text-foreground">
                <Repeat size={14} className="text-primary" /> Repeat
              </div>

              <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                {REPEAT_MODES.map(([mode, title, hint]) => (
                  <button key={mode} type="button"
                    onClick={() => (mode === 'none'
                      ? setRepeat({ mode: 'none', days: repeat.days, until: repeat.until })
                      : setMode(mode))}
                    aria-pressed={repeat.mode === mode}
                    className={`rounded-lg border p-3 text-left transition ${
                      repeat.mode === mode ? 'border-primary bg-primary/10'
                        : 'border-border hover:border-primary/60 hover:bg-muted/40'}`}>
                    <div className="flex items-center gap-2 text-sm font-medium text-foreground">
                      <span className={`flex h-4 w-4 items-center justify-center rounded-full border ${
                        repeat.mode === mode ? 'border-primary bg-primary text-primary-foreground' : 'border-border'}`}>
                        {repeat.mode === mode && <Check size={10} />}
                      </span>
                      {title}
                    </div>
                    <div className="mt-1 pl-6 text-xs text-muted-foreground">{hint}</div>
                  </button>
                ))}
              </div>

              {repeat.mode === 'days' && (
                <div className="mt-3">
                  <Label className="text-sm text-muted-foreground">Runs on</Label>
                  <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                    {WEEK.map((w, d) => (
                      <Pill key={w} on={repeat.days.includes(d)} onClick={() => toggleDay(d)}
                        title={WEEK_LONG[d]}>
                        {w}
                      </Pill>
                    ))}
                    <span className="mx-1 h-4 w-px bg-border" />
                    <button type="button" onClick={() => setRepeat((p) => ({ ...p, days: [0, 1, 2, 3, 4] }))}
                      className="text-xs text-primary hover:underline">Weekdays</button>
                    <button type="button" onClick={() => setRepeat((p) => ({ ...p, days: [5, 6] }))}
                      className="text-xs text-primary hover:underline">Weekends</button>
                  </div>
                </div>
              )}

              {/* Only a RUN has anything left to say: on "just this date" the summary printed the same answer twice. */}
              {repeat.mode !== 'none' && (
                <>
                  <div className="mt-3 flex flex-wrap items-end gap-3">
                    <div>
                      <Label className="text-sm text-muted-foreground">Until</Label>
                      <Input className="mt-1 w-44" type="date" value={repeat.until}
                        min={date} max={addDays(date, MAX_REPEAT_DAYS)}
                        onChange={(e) => setUntil(e.target.value)} />
                    </div>
                    <div className="flex flex-wrap items-center gap-1.5 pb-1">
                      {[['2 weeks', 14], ['1 month', 30], ['3 months', 91], ['6 months', 182]].map(
                        ([label, n]) => (
                          <Pill key={label} on={repeat.until === addDays(date, n)}
                            onClick={() => setUntil(addDays(date, n))}>
                            {label}
                          </Pill>
                        ))}
                    </div>
                  </div>

                  <div className="mt-3 rounded-lg border border-border bg-card p-3">
                    <div className="flex items-center gap-2 text-sm font-medium text-foreground">
                      <Repeat size={14} className="text-primary" />
                      {dates.length === 0
                        ? 'No dates yet'
                        : dates.length === 1
                          ? `One service, on ${longDate(dates[0])}`
                          : `${dates.length} services — ${shortDate(dates[0])} to ${shortDate(dates[dates.length - 1])}`}
                    </div>
                    {dates.length > 1 && (
                      <p className="mt-1 text-xs text-muted-foreground">
                        Outlined on the calendar. Dates already open are skipped, never
                        overwritten.
                      </p>
                    )}
                    {dates.length > 0 && dates[0] !== date && (
                      <p className="mt-1.5 flex items-start gap-1.5 text-xs text-amber-700">
                        <AlertTriangle size={13} className="mt-0.5 shrink-0" />
                        {longDate(date)} is not one of the ticked days, so the first service is
                        {' '}{longDate(dates[0])}.
                      </p>
                    )}
                  </div>
                </>
              )}
            </div>
          )}

          {src && (
            <div className="mt-4">
              <FromTemplate
                name={opening ? opening.template_name || opening.name : tpl.name}
                adultPrice={src.adult_price} childPrice={src.child_price}
                covers={src.covers_planned} dishes={items.length}
              />
            </div>
          )}
        </div>
      )}

      {/* ── 3 · menu items & qty ───────────────────────────────────────── */}
      {step === 'review' && (
        <div>

          {src && (
            <div className="mb-3 flex flex-wrap items-center gap-x-4 gap-y-2 rounded-lg border border-border bg-card px-3 py-2 text-sm">
              <span className="inline-flex items-center gap-1.5 text-muted-foreground">
                <Clock size={13} /> {t12(form.start_time) || '…'} – {t12(form.end_time) || '…'}
              </span>
              {/* Typed HERE too, beside the sheet they change, so the covers are set while looking at what they do. */}
              <label className="inline-flex items-center gap-1.5 text-muted-foreground">
                <Users size={13} />
                <Input className="h-8 w-16 text-right tabular-nums" type="text" inputMode="numeric"
                  value={form.adults} placeholder="0"
                  onChange={(e) => set('adults', e.target.value.replace(/\D+/g, '').slice(0, 5))}
                  aria-label="Adults this day is prepped for" />
                adults
              </label>
              <label className="inline-flex items-center gap-1.5 text-muted-foreground">
                <Input className="h-8 w-16 text-right tabular-nums" type="text" inputMode="numeric"
                  value={form.kids}
                  placeholder="0"
                  onChange={(e) => set('kids', e.target.value.replace(/\D+/g, '').slice(0, 5))}
                  aria-label="Children this day is prepped for" />
                children
              </label>
              {preview.loading && (
                <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
                  <Loader2 size={12} className="animate-spin" /> re-working the quantities…
                </span>
              )}
              <span className="text-muted-foreground">
                {dishesOut.length} dish{dishesOut.length === 1 ? '' : 'es'} going out
                {countersOut > 0 && ` · ${countersOut} live counter${countersOut === 1 ? '' : 's'}`}
              </span>
              {/* The cushion is already inside every To-prep figure — said here so nobody adds it twice. */}
              {preview.bufferPct != null && (
                <span className="text-xs text-muted-foreground"
                  title="The prep cushion set on the template's quantity step, already included in every To-prep quantity below so the counter does not run dry.">
                  incl. {preview.bufferPct}% safety buffer
                </span>
              )}
            </div>
          )}

          {items.length === 0 ? (
            <div className="rounded-lg border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
              {opening
                ? <>No dishes are recorded for this service yet — build the menu in the
                  <strong className="text-foreground"> service planner</strong>.</>
                : <>This buffet has no spread yet. Build it once in
                  <strong className="text-foreground"> Buffet Templates → the studio</strong>, and
                  every date opened from it serves the same counter.</>}
            </div>
          ) : (
            <>
              <div className="hidden px-3 pb-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground md:grid md:grid-cols-12 md:gap-2">
                <span className="md:col-span-7">Dish</span>
                <span className="md:col-span-3 text-right">To prep</span>
                <span className="md:col-span-2" />
              </div>
              <div className="max-h-[420px] space-y-3 overflow-y-auto pr-1">
                {grouped.map(([course, list]) => {
                  /* The header counts what can actually go out; the studio's
                     switched-off dishes are named apart so "8 dishes" never
                     includes one the day cannot serve. */
                  const templateOff = list.filter((i) => i.template_off).length;
                  const live = list.length - templateOff;
                  /* A live counter's rows are the station's INGREDIENTS, and
                     its header says so — "3 dishes" over paneer and butter is
                     a sheet describing itself wrongly. */
                  const isCounter = list.length > 0 && list.every((i) => i.live_counter);
                  const counterOnly = isCounter && list.every((i) => i.station_only);
                  return (
                  <div key={course}>
                    <div className="mb-1 flex items-center gap-2 px-1">
                      <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                        {course}
                      </span>
                      <span className="text-[11px] text-muted-foreground/70">
                        {counterOnly ? 'catered whole'
                          : isCounter ? `${live} ingredient${live === 1 ? '' : 's'}`
                            : `${live} dish${live === 1 ? '' : 'es'}`}
                        {templateOff > 0 && ` · ${templateOff} off in the template`}
                      </span>
                    </div>
                    <div className="overflow-hidden rounded-lg border border-border">
                      {list.map((i, ix) => {
                        /* A dish the STUDIO switched off is on the map but not
                           on offer: shown in its seat, greyed, with the reason
                           — the tick stays disabled because the day only ever
                           decides among what its template serves. Turning it
                           back on is a studio edit, and applies everywhere. */
                        if (i.template_off) {
                          return (
                            <div key={`${i.item}-${ix}`}
                              className={ix ? 'border-t border-border' : ''}>
                              <div className="grid grid-cols-1 items-center gap-1 bg-muted/40 px-3 py-2 opacity-70 md:grid-cols-12 md:gap-2">
                                <span className="flex min-w-0 items-center gap-2 md:col-span-7">
                                  <input type="checkbox" checked={false} disabled
                                    className="h-4 w-4 shrink-0"
                                    aria-label={`${i.item} is switched off in the template`} />
                                  <span className="truncate text-sm text-muted-foreground line-through">
                                    {i.item}
                                  </span>
                                </span>
                                <span className="text-xs text-muted-foreground md:col-span-3 md:text-right"
                                  title="Switch it back on in Buffet Templates → the studio — a day only decides among what its template serves.">
                                  off in the template
                                </span>
                                <div className="md:col-span-2 md:text-right">
                                  <button type="button" onClick={() => openHistory(i.item)}
                                    aria-haspopup="dialog"
                                    title={`What past services cooked and ate of ${i.item}`}
                                    className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline">
                                    <TrendingUp size={11} /> history
                                  </button>
                                </div>
                              </div>
                            </div>
                          );
                        }
                        /* A station catered whole, or an ingredient the studio
                           gave no quantity: shown, because a selected counter
                           must never vanish from the sheet in silence — but
                           with nothing to tick, since nothing is prepped for
                           it by the day either way. */
                        if (i.live_counter && i.qty == null) {
                          return (
                            <div key={`${i.item}-${ix}`}
                              className={ix ? 'border-t border-border' : ''}>
                              <div className="grid grid-cols-1 items-center gap-1 px-3 py-2 md:grid-cols-12 md:gap-2">
                                <span className="flex min-w-0 items-center gap-2 md:col-span-7">
                                  <Soup size={14} className="shrink-0 text-muted-foreground" />
                                  <span className="truncate text-sm text-foreground">{i.item}</span>
                                </span>
                                <span className="text-xs text-muted-foreground md:col-span-3 md:text-right"
                                  title={i.station_only
                                    ? (Number(i.cost_per_unit) > 0
                                      ? `This station is catered whole at ${rupees(i.cost_per_unit)} — no per-ingredient prep on the sheet`
                                      : 'This station has no per-ingredient plan — give it ingredients or an amount in the buffet studio')
                                    : "No quantity planned in the studio — set it on the buffet's Quantity per item step"}>
                                  {i.station_only ? 'station · catered whole' : 'no qty planned'}
                                </span>
                                <div className="md:col-span-2" />
                              </div>
                            </div>
                          );
                        }
                        const off = excluded.has(i.item);
                        return (
                          <div key={`${i.item}-${ix}`}
                            className={ix ? 'border-t border-border' : ''}>
                            <div className={`grid grid-cols-1 items-center gap-1 px-3 py-2 md:grid-cols-12 md:gap-2 ${
                              off ? 'bg-muted/40' : ''}`}>
                              {/* Tick = on the counter; the quantity is never typed here, or there would be a second place to look. */}
                              <label className="flex min-w-0 items-center gap-2 md:col-span-7">
                                <input type="checkbox" checked={!off}
                                  onChange={() => toggleDish(i.item)}
                                  className="h-4 w-4 shrink-0 accent-emerald-600"
                                  aria-label={`Serve ${i.item}`} />
                                <span className={`truncate text-sm ${
                                  off ? 'text-muted-foreground line-through' : 'text-foreground'}`}>
                                  {i.item}
                                </span>
                              </label>
                              <span className={`text-sm tabular-nums md:col-span-3 md:text-right ${
                                off ? 'text-muted-foreground/60 line-through' : 'text-muted-foreground'}`}>
                                {qtyLabel(i)}
                                {/* Where the figure came from: the studio's per-dish uptake, so a quantity can be argued with. */}
                                {i.uptake_pct != null && (
                                  <span className="block text-[11px] text-muted-foreground/70"
                                    title="The share of guests planned on taking this dish — set per dish on the template's quantity step.">
                                    {i.uptake_pct}% uptake
                                  </span>
                                )}
                              </span>
                              <div className="md:col-span-2 md:text-right">
                                <button type="button" onClick={() => openHistory(i.item)}
                                  aria-haspopup="dialog"
                                  title={`What past services cooked and ate of ${i.item}`}
                                  className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline">
                                  <TrendingUp size={11} /> history
                                </button>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      )}

      {/* ── the way through ────────────────────────────────────────────── */}
      <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-border/60 pt-3">
        {stepIx > 0 && (
          <button type="button" onClick={() => go(-1)} disabled={saving}
            className="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-2 text-sm text-muted-foreground hover:bg-muted">
            <ArrowLeft size={14} /> Back
          </button>
        )}
        {stepIx < steps.length - 1 ? (
          <button type="button" onClick={() => go(1)}
            disabled={saving || (step === 'buffet' && !tpl)}
            className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-60">
            Next <ArrowRight size={14} />
          </button>
        ) : (
          <button type="button" onClick={submit} disabled={saving}
            className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-60">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
            {opening ? 'Save the hours'
              : dates.length > 1 ? `Create on ${dates.length} dates` : 'Create this buffet'}
          </button>
        )}
        <button type="button" onClick={onCancel} disabled={saving}
          className="inline-flex items-center gap-1.5 rounded-md px-3 py-2 text-sm text-muted-foreground hover:bg-muted">
          <X size={14} /> Cancel
        </button>
        {/* The whole decision, in one line, from wherever you are standing. */}
        {src && (
          <span className="ml-auto hidden items-center gap-1 text-xs text-muted-foreground lg:flex">
            {form.start_time ? `${t12(form.start_time)}–${t12(form.end_time)}` : 'no hours'}
            {covers > 0 && ` · ${covers} guests`}
            {/* What is GOING OUT, not what the template holds: unticking must move this count or the line lies. */}
            {dishesOut.length > 0 && ` · ${dishesOut.length} dishes`}
            {countersOut > 0 && ` · ${countersOut} live counter${countersOut === 1 ? '' : 's'}`}
            {!opening && dates.length > 1 && ` · ${dates.length} dates`}
          </span>
        )}
      </div>

      {/* What this buffet has drawn, in its own window, carrying the averages as figures you can take. */}
      <Dialog open={bookingsOpen} onOpenChange={(o) => !o && setBookingsOpen(false)}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex flex-wrap items-center gap-2">
              <History size={16} className="text-primary" />
              {src?.name || 'This buffet'}
              <span className="font-normal text-muted-foreground">· the last 90 days</span>
            </DialogTitle>
          </DialogHeader>

          {!bookings || bookings.loading ? (
            <p className="flex items-center gap-2 py-8 text-sm text-muted-foreground">
              <Loader2 size={14} className="animate-spin" /> Reading the bookings…
            </p>
          ) : bookings.rows.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">
              Not run in the last 90 days.
            </p>
          ) : (
            <>
              <div className="flex flex-wrap items-center gap-x-5 gap-y-1.5 rounded-lg border border-border bg-muted/30 px-3 py-2 text-sm">
                <span className="text-muted-foreground">
                  <strong className="text-foreground">{bookingSummary.services}</strong> service
                  {bookingSummary.services === 1 ? '' : 's'} on record
                </span>
                {/* The figure the covers box is reaching for, with a way to take it rather than retyping it yourself. */}
                {bookings.avg > 0 && (
                  <span className="text-muted-foreground">
                    typically feeds{' '}
                    <strong className="tabular-nums text-foreground">{bookings.avg}</strong>
                    <button type="button"
                      onClick={() => { set('adults', String(bookings.avg)); setBookingsOpen(false); }}
                      className="ml-1.5 font-medium text-primary hover:underline">
                      use it
                    </button>
                  </span>
                )}
                {bookingSummary.busiest > 0 && (
                  <span className="text-muted-foreground">
                    between <strong className="tabular-nums text-foreground">{bookingSummary.quietest}</strong>
                    {' '}and <strong className="tabular-nums text-foreground">{bookingSummary.busiest}</strong> fed
                  </span>
                )}
                {bookingSummary.avgWalkIn > 0 && (
                  <span className="text-muted-foreground">
                    <strong className="tabular-nums text-foreground">{bookingSummary.avgWalkIn}</strong>{' '}
                    walk-ins a service
                  </span>
                )}
              </div>

              <div className="overflow-hidden rounded-lg border border-border">
                <table className="w-full text-sm">
                  <thead className="bg-secondary/40 text-xs text-muted-foreground">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium">Day</th>
                      <th className="px-3 py-2 text-right font-medium">Booked</th>
                      <th className="px-3 py-2 text-right font-medium">Came</th>
                      <th className="px-3 py-2 text-right font-medium">Walk-ins</th>
                      <th className="px-3 py-2 text-right font-medium">Fed in all</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bookings.rows.map((r) => {
                      // What the till counted through the door, booked arrivals included, since check-in takes the sale too.
                      const fed = Number(r.covers_sold) || 0;
                      // Booked and never turned up: the gap worth seeing when deciding how many to prep for.
                      const noShow = (Number(r.booked_heads) || 0) - (Number(r.arrived_heads) || 0);
                      return (
                        <tr key={r.date} className="border-t border-border/60">
                          <td className="whitespace-nowrap px-3 py-2 text-foreground">
                            {longDate(r.date)}
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                            {r.booked_heads || '—'}
                          </td>
                          <td className={`px-3 py-2 text-right tabular-nums ${
                            noShow > 0 ? 'text-amber-600' : 'text-muted-foreground'}`}
                            title={noShow > 0
                              ? `${noShow} booked head${noShow === 1 ? '' : 's'} did not turn up`
                              : undefined}>
                            {r.arrived_heads || '—'}
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                            {r.walk_ins || '—'}
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums font-medium text-foreground">
                            {fed || '—'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <p className="text-xs text-muted-foreground">
                Newest first. <strong className="text-foreground">Fed in all</strong> counts every
                head through the door.
              </p>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* One dish's past in its own window, so the report gets room and the spread stays where it was. */}
      <Dialog open={!!histOf} onOpenChange={(o) => !o && setHistOf(null)}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex flex-wrap items-center gap-2">
              <History size={16} className="text-primary" />
              {histOf}
              <span className="font-normal text-muted-foreground">· past services</span>
            </DialogTitle>
          </DialogHeader>

          {hist[histOf] === undefined ? (
            <p className="flex items-center gap-2 py-8 text-sm text-muted-foreground">
              <Loader2 size={14} className="animate-spin" /> Reading past services…
            </p>
          ) : hist[histOf].length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">
              <strong className="text-foreground">{histOf}</strong> has never been on a counter.
              Its quantity comes from setup.
            </p>
          ) : (
            <>
              {histSummary && (
                <div className="flex flex-wrap items-center gap-x-5 gap-y-1.5 rounded-lg border border-border bg-muted/30 px-3 py-2 text-sm">
                  <span className="text-muted-foreground">
                    <strong className="text-foreground">{histSummary.services}</strong> service
                    {histSummary.services === 1 ? '' : 's'} on record
                  </span>
                  {histSummary.avg != null && (
                    <span className="text-muted-foreground">
                      eaten on average{' '}
                      <strong className="text-foreground tabular-nums">
                        {histSummary.unit === 'pcs'
                          ? `${Math.round(histSummary.avg)} pcs`
                          : `${histSummary.avg.toFixed(1)} ${histSummary.unit}`}
                      </strong>
                    </span>
                  )}
                  {histSummary.over > 0 && (
                    <span className="text-amber-700">
                      ran past what was cooked <strong>{histSummary.over}×</strong>
                    </span>
                  )}
                </div>
              )}

              <div className="overflow-hidden rounded-lg border border-border">
                <table className="w-full text-sm">
                  <thead className="bg-secondary/40 text-xs text-muted-foreground">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium">Day</th>
                      <th className="px-3 py-2 text-left font-medium">Buffet</th>
                      <th className="px-3 py-2 text-right font-medium">Planned</th>
                      <th className="px-3 py-2 text-right font-medium">Eaten</th>
                    </tr>
                  </thead>
                  <tbody>
                    {hist[histOf].map((r, rx) => {
                      // Eaten above plan: the pan ran past what was cooked, the row worth spotting when judging a quantity.
                      const over = r.planned_qty != null
                        && Number(r.consumed_qty) > Number(r.planned_qty);
                      return (
                        <tr key={`${r.date}-${r.buffet_id}-${rx}`} className="border-t border-border/60">
                          <td className="whitespace-nowrap px-3 py-2">{longDate(r.date)}</td>
                          <td className="px-3 py-2 text-muted-foreground">{r.buffet_name}</td>
                          <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                            {r.planned_qty != null ? `${r.planned_qty} ${r.unit}` : '—'}
                          </td>
                          <td className={`px-3 py-2 text-right tabular-nums ${
                            over ? 'font-semibold text-amber-600' : 'text-foreground'}`}
                            title={over ? 'Guests ate more than was cooked — it ran out' : undefined}>
                            {r.consumed_qty} {r.unit}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <p className="text-xs text-muted-foreground">
                Every buffet, newest first. Amber: guests ate past what was cooked.
              </p>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
